import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCommonTranslation } from 'c-translation';
import ApiClient from 'c-data/apiClient';
import to from 'await-to-js';

import { useAPIClientRequest } from 'c-hooks';

import type { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
    Box,
    CircularProgress,
    IconButton,
    InputAdornment,
    Stack,
    Tab,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Alert,
    Button,
    Chip,
    DialogV2,
    EntityAutocomplete,
    TextAreaField,
    TextField,
    Autocomplete,
} from 'c-components';
import type { UseBoolean } from 'react-hanger';
import { useBoolean } from 'react-hanger';
import { TabContext, TabList } from '@mui/lab';
import { ContentCopy, Search } from '@mui/icons-material';
import type {
    CampaignMediaGroupParameter,
    CampaignMediaGroupParameterName,
    CampaignMediaGroupParameterSearchType,
    CampaignMediaGroupName,
    MediaTable as MediaTableList,
} from 'c-sdk';

import PopSettingsCreativesSearch from 'c-main/Components/Schedule/Components/PopSettings/PopSettingsCreativesSearch';
import ParamsTable from 'c-main/Components/Schedule/Components/MediaTable/ParamsTable';

import MediaTableGroups from 'c-main/Components/Schedule/Components/MediaTable/MediaTableGroups';
import ClassicDialog from 'c-main/Components/Schedule/Components/MediaTable/ClassicDialog';
import UseAPIClientRequest from '../../../../../../Hooks/useAPIClientRequest';

type Props = {
    campaignId: number;
    addNameState: UseBoolean;
    startDate: string;
    endDate: string;
    campaignMediaGroupNames?: CampaignMediaGroupName[];
    campaignMediaGroupParameters?: CampaignMediaGroupParameter[];
    refreshCampaignGroups?: () => Promise<any>;
};

type GroupParameterDraft = {
    parameter: CampaignMediaGroupParameterName;
    searchType: CampaignMediaGroupParameterSearchType;
    identifiers: string[];
};

const normalizeDrafts = (drafts: GroupParameterDraft[]) => {
    const seen = new Set<string>();

    return drafts.reduce<GroupParameterDraft[]>((acc, draft) => {
        const normalizedDraft = {
            parameter: draft.parameter,
            searchType: draft.searchType,
            identifiers: Array.from(new Set(draft.identifiers.filter(Boolean))),
        };

        if (normalizedDraft.identifiers.length === 0) {
            return acc;
        }

        const key = `${normalizedDraft.parameter}::${
            normalizedDraft.searchType
        }::${normalizedDraft.identifiers.join('|')}`;
        if (seen.has(key)) {
            return acc;
        }

        seen.add(key);
        acc.push(normalizedDraft);
        return acc;
    }, []);
};

const serializeDraft = (draft: GroupParameterDraft) =>
    `${draft.parameter}::${draft.searchType}::${draft.identifiers.join('|')}`;

const normalizeExistingParameterToDraft = (
    groupParameter: CampaignMediaGroupParameter,
): GroupParameterDraft => ({
    parameter: groupParameter.parameter,
    searchType: groupParameter.search_type,
    identifiers:
        groupParameter.search_type === 'contains_all'
            ? groupParameter.identifier
                  .split('%')
                  .map(value => value.trim())
                  .filter(Boolean)
            : [groupParameter.identifier],
});

const isCombinedSearchType = (searchType: CampaignMediaGroupParameterSearchType) =>
    searchType === 'contains_all';

const buildGroupParameterPayloads = (groupId: number, configs: GroupParameterDraft[]) =>
    configs.map(config => ({
        group_id: groupId,
        parameter: config.parameter,
        search_type: config.searchType,
        identifier: isCombinedSearchType(config.searchType)
            ? [`%${config.identifiers.join('%')}%`]
            : config.identifiers,
    }));

const tabLabels = [
    { label: 'ID', field: 'id', translation: 'id' },
    { label: 'Name', field: 'name', translation: 'name' },
    { label: 'Media Ext ID', field: 'media_extid', translation: 'mediaExtId' },
    { label: 'Creative Ref', field: 'creative_ref', translation: 'creativeRef' },
    { label: 'Campaign Name', field: 'campaign_name', translation: 'campaignName' },
    { label: 'Campaign Ext ID', field: 'campaign_extid', translation: 'campaignExtId' },
    { label: 'Campaign Ref', field: 'campaign_ref', translation: 'campaignRef' },
];

const MediaTable: React.FC<Props> = ({
    campaignId,
    addNameState,
    startDate,
    endDate,
    campaignMediaGroupNames = [],
    campaignMediaGroupParameters = [],
    refreshCampaignGroups,
}) => {
    const t = useCommonTranslation();
    const [activeTab, setActiveTab] = useState('0');
    const [paramsData, setParamsData] = useState([]);
    const [tabContent, setTabContent] = useState<{ [key: string]: string[] }>({});
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedParameter, setSelectedParameter] = useState('');
    const [activeGroup, setActiveGroup] = useState<string | null>('All');
    const [deletedGroupIds, setDeletedGroupIds] = useState<number[]>([]);
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [searchType, setSearchType] = useState<
        'exact' | 'contains' | 'starts_with' | 'ends_with'
    >('contains');
    const [selectedLogImportSourceId, setSelectedLogImportSourceId] = useState<number | 'any'>(
        'any',
    );
    const visibleCampaignMediaGroupNames = useMemo(
        () => campaignMediaGroupNames.filter(group => !deletedGroupIds.includes(group.id)),
        [campaignMediaGroupNames, deletedGroupIds],
    );
    const campaignGroups = useMemo(
        () =>
            visibleCampaignMediaGroupNames.reduce<Record<string, number>>((acc, group) => {
                acc[group.name] = group.campaignMediaGroup?.length ?? 0;
                return acc;
            }, {}),
        [visibleCampaignMediaGroupNames],
    );
    const campaignMediaGroupNamesById = useMemo(
        () =>
            visibleCampaignMediaGroupNames.reduce<Record<number, CampaignMediaGroupName>>(
                (acc, group) => {
                    acc[group.id] = group;
                    return acc;
                },
                {},
            ),
        [visibleCampaignMediaGroupNames],
    );
    const campaignMediaGroupWarningsByName = useMemo(
        () =>
            visibleCampaignMediaGroupNames.reduce<Record<string, string[]>>((acc, group) => {
                const stringWarnings =
                    typeof group.warning === 'string'
                        ? group.warning
                              .split(';')
                              .map(value => value.trim())
                              .filter(Boolean)
                        : [];
                const warnings = [
                    ...(Array.isArray(group.warnings) ? group.warnings : []),
                    ...stringWarnings,
                ].filter(Boolean) as string[];

                if (warnings.length > 0) {
                    acc[group.name] = warnings;
                }

                return acc;
            }, {}),
        [visibleCampaignMediaGroupNames],
    );
    const campaignMediaGroupParametersByGroupName = useMemo(
        () =>
            campaignMediaGroupParameters.reduce<Record<string, CampaignMediaGroupParameter[]>>(
                (acc, groupParameter) => {
                    const groupId = groupParameter.group_name_id ?? groupParameter.group_id;
                    const groupName = groupId
                        ? campaignMediaGroupNamesById[groupId]?.name
                        : undefined;

                    if (!groupName) {
                        return acc;
                    }

                    acc[groupName] = [...(acc[groupName] ?? []), groupParameter];
                    return acc;
                },
                {},
            ),
        [campaignMediaGroupNamesById, campaignMediaGroupParameters],
    );
    const campaignMediaGroupIdsByName = useMemo(
        () =>
            visibleCampaignMediaGroupNames.reduce<Record<string, number>>((acc, group) => {
                acc[group.name] = group.id;
                return acc;
            }, {}),
        [visibleCampaignMediaGroupNames],
    );
    const campaignMediaGroupMediaIdsByName = useMemo(
        () =>
            visibleCampaignMediaGroupNames.reduce<Record<string, Set<number>>>((acc, group) => {
                acc[group.name] = new Set(
                    (group.campaignMediaGroup ?? []).map(campaignMediaGroup =>
                        Number(campaignMediaGroup.media_id),
                    ),
                );
                return acc;
            }, {}),
        [visibleCampaignMediaGroupNames],
    );
    const {
        start: groupStart,
        error: groupError,
        isLoading: groupLoading,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.editGroup);
    const {
        start: addMediaGroupParameterStart,
        error: addMediaGroupParameterError,
        isLoading: addMediaGroupParameterLoading,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.addMediaGroupParameter);
    const {
        start: deleteMediaGroupsStart,
        error: deleteMediaGroupsError,
        isLoading: deleteMediaGroupsLoading,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.deleteMediaGroups);
    const {
        start: deleteMediaGroupParametersStart,
        error: deleteMediaGroupParameterError,
        isLoading: deleteMediaGroupParametersLoading,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.deleteMediaGroupParameters);
    const handleTabChange = useCallback(
        (event: React.SyntheticEvent, newValue: number) => {
            setActiveTab(newValue.toString());
        },
        [setActiveTab],
    );
    const paramDialogState = useBoolean(false);
    const [tableData, setTableData] = useState<MediaTableList[]>([]);
    const groupedMediaIds = useMemo(
        () =>
            new Set(
                Object.values(campaignMediaGroupMediaIdsByName).flatMap(mediaIds =>
                    Array.from(mediaIds),
                ),
            ),
        [campaignMediaGroupMediaIdsByName],
    );
    const ungroupedMediaCount = useMemo(
        () => tableData.filter(row => !groupedMediaIds.has(Number(row.id))).length,
        [groupedMediaIds, tableData],
    );
    const allMediaNames = useMemo(() => tableData.map(media => media.name), [tableData]);
    const filteredTableData = useMemo(
        () =>
            tableData.filter(row => {
                const matchesSearch = Object.values(row).some(value =>
                    value?.toString().toLowerCase().includes(searchQuery.toLowerCase()),
                );

                const matchesParameter = selectedParameter
                    ? row.parameter?.some(param => param?.parameter === selectedParameter)
                    : true;
                const matchesGroup =
                    activeGroup !== 'All'
                        ? campaignMediaGroupMediaIdsByName[activeGroup]?.has(Number(row.id)) ??
                          row.group_name === activeGroup
                        : true;

                return matchesSearch && matchesParameter && matchesGroup;
            }),
        [tableData, searchQuery, selectedParameter, activeGroup, campaignMediaGroupMediaIdsByName],
    );
    const groupActionLoading =
        groupLoading ||
        addMediaGroupParameterLoading ||
        deleteMediaGroupsLoading ||
        deleteMediaGroupParametersLoading;

    const cols: GridColDef[] = [
        {
            field: 'id',
            headerName: t('Modules.Main.Campaigns.CampaignSchedule.popSettings.id'),
            width: 90,
        },
        {
            field: 'name',
            headerName: t('Modules.Main.Campaigns.CampaignSchedule.popSettings.name'),
            renderHeader: () => (
                <Stack direction="row" alignItems="center" gap={1}>
                    <Box fontWeight={700}>
                        {t('Modules.Main.Campaigns.CampaignSchedule.popSettings.name')}
                    </Box>
                    <Tooltip
                        title={t(
                            'Modules.Main.Campaigns.CampaignSchedule.mediaTable.copyAllMediaNames',
                        )}
                    >
                        <IconButton
                            size="small"
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigator.clipboard.writeText(allMediaNames.join('\n'));
                            }}
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
            width: 200,
        },
        {
            field: 'creative_ref',
            headerName: t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.mediaRef'),
            width: 150,
        },
        {
            field: 'logImportSource.name',
            headerName: t('Modules.Main.Campaigns.CampaignSchedule.popSettings.source'),
            width: 150,
            renderCell: ({ row }) => <Box>{row?.logImportSource?.name}</Box>,
        },
        {
            field: 'first_seen',
            headerName: t('Modules.Main.Campaigns.CampaignSchedule.popSettings.firstSeen'),
            width: 150,
        },
        {
            field: 'last_seen',
            headerName: t('Modules.Main.Campaigns.CampaignSchedule.popSettings.lastSeen'),
            width: 150,
        },
        {
            field: 'parameter',
            flex: 1,
            minWidth: 240,
            headerName: t('Modules.Main.Campaigns.CampaignSchedule.popSettings.parameters'),
            renderCell: ({ row }) => (
                <Stack gap={1} direction="row">
                    {row?.parameter?.map((param: any) => {
                        if (param != null) {
                            return (
                                <Chip
                                    key={param?.id}
                                    label={`${param.parameter}: ${param.identifier}`}
                                />
                            );
                        }
                        return '';
                    })}
                </Stack>
            ),
        },
    ];
    const { start, data, isLoading, error } = UseAPIClientRequest(
        ApiClient.Entities.Campaign.mediaList,
    );
    const {
        start: getParamStart,
        error: paramsGetError,
        isLoading: getParmasLoading,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.GetMediaParameter);
    const {
        start: updateParamsStart,
        error: updateError,
        isLoading: updateLoading,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.UpdateMediaParameter);
    const {
        start: delParam,
        error: delError,
        isLoading: delLoading,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.DeleteMultipleMediaParameters);

    const {
        start: syncStart,
        data: syncData,
        error: syncError,
        isLoading: syncLoading,
    } = UseAPIClientRequest(ApiClient.Entities.Campaign.syncMedia);
    const searchTypeOptions = useMemo(
        () => [
            {
                label: t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.searchType.contains'),
                value: 'contains',
            },
            {
                label: t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.searchType.exact'),
                value: 'exact',
            },
            {
                label: t(
                    'Modules.Main.Campaigns.CampaignSchedule.mediaTable.searchType.startsWith',
                ),
                value: 'starts_with',
            },
            {
                label: t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.searchType.endsWith'),
                value: 'ends_with',
            },
        ],
        [t],
    );

    const getMediaListData = useCallback(async () => {
        const [err, succ] = await to(start(campaignId));
        if (succ) {
            const uniqueData = Array.from(
                new Map(succ.data.media.map(item => [item.id, item])).values(),
            ); // Ensure no duplicates
            setTableData(uniqueData);
        }
    }, [campaignId, start]);
    useEffect(() => {
        if (addNameState.value) {
            getMediaListData();
            addNameState.setFalse();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addNameState.value]);
    useEffect(() => {
        getMediaListData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getParams = useCallback(async () => {
        const [err, succ] = await to(getParamStart(campaignId));
        if (succ) {
            setParamsData(succ.data.data);
        }
    }, [campaignId, getParamStart]);

    const refreshData = useCallback(async () => {
        await to(getParams());
        await to(refreshCampaignGroups?.());
        await to(getMediaListData());
    }, [getMediaListData, getParams, refreshCampaignGroups]);
    const handleAttachSelectionToGroup = useCallback(
        async (groupName: string) => {
            const groupId = campaignMediaGroupIdsByName[groupName];
            if (groupId == null || rowSelectionModel.length === 0) return;
            const identifiers = rowSelectionModel.map(value => String(value));
            await to(
                addMediaGroupParameterStart(campaignId, {
                    group_id: groupId,
                    parameter: 'id',
                    search_type: 'exact',
                    identifier: identifiers,
                } as any),
            );
            setRowSelectionModel([]);
            await refreshData();
        },
        [
            addMediaGroupParameterStart,
            campaignId,
            campaignMediaGroupIdsByName,
            refreshData,
            rowSelectionModel,
        ],
    );

    const handleCreateGroup = useCallback(
        async (group_name: string, groupParameterConfigs: GroupParameterDraft[] = []) => {
            const [, createSucc] = await to(
                groupStart(campaignId, {
                    group_name,
                    media_ids: [],
                }),
            );

            if (!createSucc) return;

            const normalizedConfigs = normalizeDrafts(
                groupParameterConfigs.filter(config => config.identifiers.length > 0),
            );

            if (normalizedConfigs.length > 0) {
                const [, campaignSucc] = await to(refreshCampaignGroups?.());
                const createdGroup = campaignSucc?.data?.data?.campaignMediaGroupName?.find(
                    (group: CampaignMediaGroupName) => group.name === group_name,
                );

                if (createdGroup) {
                    const payloads = buildGroupParameterPayloads(
                        createdGroup.id,
                        normalizedConfigs,
                    );

                    // eslint-disable-next-line no-restricted-syntax
                    for (const payload of payloads) {
                        // eslint-disable-next-line no-await-in-loop
                        await to(addMediaGroupParameterStart(campaignId, payload));
                    }
                }
            }

            await refreshData();
        },
        [addMediaGroupParameterStart, campaignId, groupStart, refreshCampaignGroups, refreshData],
    );
    const handleDeleteGroupParameter = useCallback(
        async (groupParameterId: number) => {
            const [, succ] = await to(
                deleteMediaGroupParametersStart(campaignId, {
                    group_parameter_ids: [groupParameterId],
                }),
            );

            if (succ) {
                await refreshData();
            }
        },
        [campaignId, deleteMediaGroupParametersStart, refreshData],
    );
    const handleUpdateGroupParameters = useCallback(
        async (
            groupName: string,
            groupParameters: CampaignMediaGroupParameter[],
            configs: GroupParameterDraft[],
        ) => {
            const groupId = campaignMediaGroupIdsByName[groupName];
            if (groupId == null) return;

            const normalizedConfigs = normalizeDrafts(
                configs.filter(config => config.identifiers.length > 0),
            );
            const existingDrafts = groupParameters.map(normalizeExistingParameterToDraft);
            const desiredKeys = new Set(normalizedConfigs.map(serializeDraft));
            const existingKeys = new Set(existingDrafts.map(serializeDraft));

            const groupParameterIdsToDelete = groupParameters
                .filter(
                    parameter =>
                        !desiredKeys.has(
                            serializeDraft(normalizeExistingParameterToDraft(parameter)),
                        ),
                )
                .map(parameter => parameter.id);

            const configsToCreate = normalizedConfigs.filter(
                config => !existingKeys.has(serializeDraft(config)),
            );

            if (groupParameterIdsToDelete.length > 0) {
                const [, deleteSucc] = await to(
                    deleteMediaGroupParametersStart(campaignId, {
                        group_parameter_ids: groupParameterIdsToDelete,
                    }),
                );

                if (!deleteSucc) return;
            }

            if (normalizedConfigs.length === 0) {
                await refreshData();
                return;
            }

            if (configsToCreate.length === 0) {
                await refreshData();
                return;
            }

            const payloads = buildGroupParameterPayloads(groupId, configsToCreate);

            // eslint-disable-next-line no-restricted-syntax
            for (const payload of payloads) {
                // eslint-disable-next-line no-await-in-loop
                await to(addMediaGroupParameterStart(campaignId, payload));
            }

            await refreshData();
        },
        [
            addMediaGroupParameterStart,
            campaignId,
            campaignMediaGroupIdsByName,
            deleteMediaGroupParametersStart,
            refreshData,
        ],
    );
    const handleDeleteGroup = useCallback(
        async (groupId: number, groupName: string) => {
            const [, succ] = await to(
                deleteMediaGroupsStart(campaignId, {
                    group_name_ids: [groupId],
                }),
            );

            if (succ) {
                setDeletedGroupIds(prev => [...prev, groupId]);
                if (activeGroup === groupName) {
                    setActiveGroup('All');
                }
                await to(refreshCampaignGroups?.());
                await refreshData();
            }
        },
        [activeGroup, campaignId, deleteMediaGroupsStart, refreshCampaignGroups, refreshData],
    );

    const handleDialogOpen = useCallback(() => {
        setTabContent({});
        setSelectedLogImportSourceId('any');
        getParams();
        paramDialogState.setTrue();
    }, [paramDialogState, getParams]);

    const handleApplyChanges = useCallback(async () => {
        const updatedFields = Object.keys(tabContent).reduce((acc, field) => {
            if (tabContent[field] && tabContent[field].length > 0) {
                acc[field] = tabContent[field];
            }
            return acc;
        }, {} as { [key: string]: string[] });

        // eslint-disable-next-line no-restricted-syntax
        for (const [field, identifiers] of Object.entries(updatedFields)) {
            const payload = {
                parameter: field as
                    | 'id'
                    | 'name'
                    | 'media_extid'
                    | 'creative_ref'
                    | 'campaign_name'
                    | 'campaign_extid'
                    | 'campaign_ref',
                identifier: identifiers,
                search_type: searchType,
                ...(selectedLogImportSourceId === 'any'
                    ? {}
                    : { log_import_source_id: Number(selectedLogImportSourceId) }),
            };

            // eslint-disable-next-line no-await-in-loop
            await to(updateParamsStart(campaignId, payload));
        }
        paramDialogState.setFalse();
        await to(refreshData());
    }, [
        tabContent,
        paramDialogState,
        refreshData,
        searchType,
        selectedLogImportSourceId,
        updateParamsStart,
        campaignId,
    ]);

    const handleTabContentChange = useCallback((field: string, value: string) => {
        setTabContent(prev => ({
            ...prev,
            [field]: value.split('\n').filter(v => v.trim()),
        }));
    }, []);

    const resetButton = useMemo(
        () => (
            <Button size="small" onClick={() => setSearchQuery('')}>
                <Typography variant="caption">
                    {t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.clear')}
                </Typography>
            </Button>
        ),
        [t],
    );
    const handleDeleteSelected = useCallback(
        async (ids: number[]) => {
            if (ids.length === 0) return;
            const [err, succ] = await to(delParam(campaignId, ids));
            if (succ) await refreshData();
        },
        [campaignId, delParam, refreshData],
    );
    const handleSync = useCallback(async () => {
        await to(syncStart(campaignId));
        await to(refreshData());
    }, [campaignId, refreshData, syncStart]);
    const handleExportCSV = () => {
        const headers = [
            'ID',
            'Name',
            'Media Ref',
            'Source',
            'First Seen',
            'Last Seen',
            'Parameters',
        ];
        const rows = tableData.map(row => [
            row.id,
            `"${row.name}"`,
            `"${(row as any)?.creative_ref ?? ''}"`,
            `"${(row as any)?.logImportSource?.name || ''}"`,
            row.first_seen || '',
            row.last_seen || '',
            row.parameter?.map(param => `${param.parameter}: ${param.identifier}`).join('; ') || '',
        ]);

        const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `media_table_export_${new Date().toISOString()}.csv`;
        link.click();
    };

    const openParamsDialogFromCell = useCallback(
        (field: string, value: string) => {
            const idx = tabLabels.findIndex(t => t.field === field);
            if (idx !== -1) {
                setActiveTab(idx.toString());
            }

            setSearchType('exact');
            setSelectedLogImportSourceId('any');

            setTabContent(prev => ({
                ...prev,
                [field]: value ? [value] : [],
            }));

            getParams();

            paramDialogState.setTrue();
        },
        [getParams, paramDialogState, setActiveTab],
    );
    const onCloseParamDialog = useCallback(() => {
        paramDialogState.setFalse();
        setSearchType('contains');
        setSelectedLogImportSourceId('any');
        setTabContent({});
    }, [paramDialogState]);

    return (
        <>
            <DialogV2
                onClose={onCloseParamDialog}
                open={paramDialogState.value}
                title="Modules.Main.Campaigns.CampaignSchedule.mediaTable.editParams"
                maxWidth="xl"
            >
                {paramsGetError && <Alert severity="error">{String(paramsGetError)}</Alert>}
                <TabContext value={activeTab.toString()}>
                    <TabList onChange={handleTabChange}>
                        {tabLabels.map((tab, index) => (
                            <Tab key={tab.label} label={tab.label} value={index.toString()} />
                        ))}
                    </TabList>

                    <Box mt={2}>
                        {tabLabels.map((tab, index) => (
                            <Box
                                key={tab.label}
                                hidden={activeTab !== index.toString()}
                                display={activeTab === index.toString() ? 'block' : 'none'}
                            >
                                <Typography variant="body1" gutterBottom>
                                    {t(
                                        `Modules.Main.Campaigns.CampaignSchedule.popSettings.${tab.translation}`,
                                    )}
                                </Typography>
                                <Box display="flex" alignItems="flex-end" gap={2}>
                                    <Box flex={1}>
                                        <Autocomplete
                                            textFieldProps={{
                                                label: t(
                                                    'Modules.Main.Campaigns.CampaignSchedule.mediaTable.paramsEditDialog.searchType',
                                                ),
                                                size: 'small',
                                            }}
                                            options={searchTypeOptions}
                                            value={searchType}
                                            onChange={value =>
                                                setSearchType(
                                                    value as
                                                        | 'exact'
                                                        | 'contains'
                                                        | 'starts_with'
                                                        | 'ends_with',
                                                )
                                            }
                                        />
                                    </Box>
                                    <Box flex={1}>
                                        <EntityAutocomplete
                                            entityName="PopDisplays_LogImportSource"
                                            labelColumn="name"
                                            searchColumns={['name']}
                                            textFieldProps={{
                                                label: 'Log Import Source',
                                                size: 'small',
                                            }}
                                            additionalOptions={[{ label: 'Any', value: 'any' }]}
                                            value={selectedLogImportSourceId}
                                            onChange={value => {
                                                setSelectedLogImportSourceId(
                                                    value === 'any' ? 'any' : Number(value),
                                                );
                                            }}
                                            multiple={false}
                                        />
                                    </Box>
                                </Box>

                                <TextAreaField
                                    multiline
                                    fullWidth
                                    rows={5}
                                    value={tabContent[tab.field]?.join('\n') || ''}
                                    onChange={e =>
                                        handleTabContentChange(tab.field, e.target.value)
                                    }
                                    variant="outlined"
                                />
                                <Box mt={2} display="flex" justifyContent="flex-end">
                                    {!updateLoading ? (
                                        <Button variant="contained" onClick={handleApplyChanges}>
                                            {t(
                                                'Modules.Main.Campaigns.CampaignSchedule.mediaTable.applyChanges',
                                            )}
                                        </Button>
                                    ) : (
                                        <CircularProgress size={20} />
                                    )}
                                </Box>

                                <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                                    {getParmasLoading || delLoading ? (
                                        <CircularProgress />
                                    ) : (
                                        <ParamsTable
                                            params={paramsData?.filter(
                                                param => param.parameter === tab.field,
                                            )}
                                            param={tab.field}
                                            loading={delLoading}
                                            onDeleteSelected={handleDeleteSelected}
                                            getParamsLoading={getParmasLoading}
                                            delError={delError}
                                        />
                                    )}
                                </Box>
                            </Box>
                        ))}
                        {delError && <Alert severity="error">{String(delError)}</Alert>}
                    </Box>
                </TabContext>
                {updateError && <Alert severity="error">{String(updateError)}</Alert>}
            </DialogV2>
            {groupError && <Alert severity="error">{String(groupError)}</Alert>}
            {addMediaGroupParameterError && (
                <Alert severity="error">{String(addMediaGroupParameterError)}</Alert>
            )}
            {deleteMediaGroupsError && (
                <Alert severity="error">{String(deleteMediaGroupsError)}</Alert>
            )}
            {deleteMediaGroupParameterError && (
                <Alert severity="error">{String(deleteMediaGroupParameterError)}</Alert>
            )}
            <Stack direction="row" gap={1} sx={{ width: '100%', overflow: 'hidden' }}>
                <Box minWidth="15rem">
                    <MediaTableGroups
                        mediaGroups={campaignGroups}
                        groupIdsByName={campaignMediaGroupIdsByName}
                        activeGroup={activeGroup}
                        setActiveGroup={setActiveGroup}
                        totalMediaCount={tableData.length}
                        ungroupedMediaCount={ungroupedMediaCount}
                        handleCreateGroup={handleCreateGroup}
                        handleDeleteGroup={handleDeleteGroup}
                        handleDeleteGroupParameter={handleDeleteGroupParameter}
                        handleUpdateGroupParameters={handleUpdateGroupParameters}
                        campaignMediaGroupParametersByGroupName={
                            campaignMediaGroupParametersByGroupName
                        }
                        groupWarningsByName={campaignMediaGroupWarningsByName}
                        groupActionLoading={groupActionLoading}
                        selectedMediaIds={rowSelectionModel.map(value => String(value))}
                        onAttachSelectionToGroup={handleAttachSelectionToGroup}
                    />
                </Box>
                <Box
                    flex={1}
                    minWidth={0}
                    pr={2}
                    sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" pb={2}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <TextField
                                variant="outlined"
                                placeholder={t(
                                    'Modules.Main.Campaigns.CampaignSchedule.mediaTable.search',
                                )}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery && (
                                        <InputAdornment position="end">
                                            {resetButton}
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: '300px' }}
                            />
                        </Box>
                        <Stack direction="row" gap={1}>
                            <Stack>
                                <Button onClick={handleSync}>
                                    {t(
                                        'Modules.Main.Campaigns.CampaignSchedule.mediaTable.syncMedia',
                                    )}
                                </Button>
                                {syncError && <Alert severity="error">{String(syncError)}</Alert>}
                            </Stack>
                            <Button variant="contained" onClick={handleDialogOpen}>
                                {t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.editParams')}
                            </Button>
                            <ClassicDialog campaignId={campaignId} />
                            <Button variant="contained" onClick={handleExportCSV}>
                                {t('Modules.Main.Campaigns.CampaignSchedule.mediaTable.exportCsv')}
                            </Button>

                            <PopSettingsCreativesSearch
                                campaignId={campaignId}
                                startDate={startDate}
                                endDate={endDate}
                                mediaTableData={tableData}
                                onApplyChanges={refreshData}
                                onOpenParamDialog={openParamsDialogFromCell}
                            />
                        </Stack>
                    </Box>
                    <DataGridPro
                        columns={cols}
                        rows={filteredTableData}
                        loading={isLoading || syncLoading}
                        autoHeight
                        disableColumnResize={false}
                        disableRowSelectionOnClick
                        checkboxSelection
                        rowSelectionModel={rowSelectionModel}
                        onRowSelectionModelChange={setRowSelectionModel}
                    />
                    {error && <Alert severity="error">{String(error)}</Alert>}
                </Box>
            </Stack>
        </>
    );
};

export default MediaTable;
